/**
 * RecordsBlock — the product's own records, in the chat, as the page shows
 * them.
 *
 * Runs and spans reuse the page's own tables (AgentRunsTable, SpansTable,
 * with the page's cell renderers); every other entity renders through
 * GenericRecordsTable with the columns the page's list uses. A row opens
 * where the page would open it; the footer opens the page itself, with the
 * filters the lookup used.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import AgentRunsTable from 'features/agent-analytics/components/AgentRunsTable';
import SpansTable from 'features/agent-analytics/components/SpansTable';
import { useTeam } from 'shared/context/teamContext';
import { trodoAnalyticsTheme as theme } from 'features/analytics/studio/theme';
import EvalsTable from 'features/evals3/components/EvalsTable';
import GenericRecordsTable from './GenericRecordsTable';
import RecordPopup from './RecordPopup';
import { ENTITY_PAGES, ROW_LINKS, columnsFor, openHref, openLabel } from './recordColumns';
import { RecordsSurfaceProvider, openInChat, openBesideChat, besideClick } from './recordsSurface';
import ChatPager, { usePaged, BLOCK_TITLE_STYLE } from 'shared/components/chat/ChatPager';

// Loaded on demand: the comment card pulls in the markdown renderer, which an
// answer with no comments in it should not pay for.
const AnnotationRecords = React.lazy(() => import('./AnnotationRecords'));

/**
 * A ROW OPENS IN PLACE, NEVER ON ANOTHER PAGE. Runs and spans go through the
 * chat page's own entity opener — the same full run view a run id opens in
 * prose — and every other record opens as a popup built from the row itself.
 * The "Open in <page>" link is the one explicit way to leave the chat.
 *
 * The tables below are the PAGE's tables, so a cell inside one can still hold
 * a real navigating link. RecordsSurfaceProvider tells those cells they are in
 * the chat; openInChat itself now lives in recordsSurface beside them.
 */

export default function RecordsBlock({ block }) {
  const { selectedTeam } = useTeam();
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || String(selectedTeam?.id ?? selectedTeam?._id ?? '');
  const entity = block.entity;
  const rows = Array.isArray(block.rows) ? block.rows : [];
  const page = ENTITY_PAGES[entity];
  const href = openHref(teamSlug, entity, block.open);
  const linkLabel = openLabel(entity, block.open);
  const [popup, setPopup] = useState(null);
  // Up to 50 records arrive; 25 render at a time, like every chat table.
  const paged = usePaged(rows);
  /**
   * A comment opens what it is ABOUT.
   *
   * Every other entity is a thing in itself, so the field-dump popup is the
   * right answer for it. A comment is not: clicking one and getting a panel
   * that reads back `body`, `context_type`, `run_id` tells you nothing you
   * could not already see in the row. What you want is the span or run the
   * remark was left on — which now shows the comment in its Activity, so the
   * click lands on the comment in its own context.
   */
  const open = (row) => {
    if (entity === 'runs') return openInChat('run', row.run_id);
    if (entity === 'spans') return openInChat('span', row.span_id);
    if (entity === 'annotations') {
      if (row.context_type === 'span' && row.span_id) return openInChat('span', row.span_id);
      if (row.run_id) return openInChat('run', row.run_id);
      // A conversation-level comment has no run or span to open; the row's
      // own fields are then genuinely all there is.
      return setPopup(row);
    }
    return setPopup(row);
  };

  /**
   * A comment in the chat reads the way a comment reads.
   *
   * Rendering comments as a grid put the body in a truncated cell beside a
   * run id, lost the replies entirely — a reply appeared as another top-level
   * row — and showed none of the thread. They render with the product's own
   * comment thread instead: author, time, what it is on, and its replies
   * folded under it, the same component the run's Activity uses.
   */
  let table;
  if (entity === 'annotations' && Array.isArray(block.threads) && block.threads.length) {
    table = (
      <React.Suspense fallback={null}>
        <AnnotationRecords threads={block.threads} />
      </React.Suspense>
    );
  } else if (entity === 'evals') {
    // The Evals page's own table; a row opens that eval, as it does there.
    table = <EvalsTable rows={paged.visible} onOpen={(r) => { const to = ROW_LINKS.evals(r); if (to) openBesideChat(`/${encodeURIComponent(teamSlug)}${to}`); }} />;
  } else if (entity === 'runs') {
    table = <AgentRunsTable rows={paged.visible} columnKeys={(block.columns || []).map((c) => c.key)} onRowClick={open} emptyMessage="No runs match." />;
  } else if (entity === 'spans') {
    table = <SpansTable rows={paged.visible} columnKeys={(block.columns || []).map((c) => c.key)} onRowClick={open} onRunClick={(id) => openInChat('run', id)} onSpanClick={(id) => openInChat('span', id)} emptyMessage="No spans match." />;
  } else {
    table = <GenericRecordsTable rows={paged.visible} columns={columnsFor(entity, block.columns)} onRowClick={open} />;
  }

  const count = block.total != null ? block.total : rows.length;
  return (
    <div className="trodo-records-block" data-entity={entity} style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={BLOCK_TITLE_STYLE}>
          {block.title || page?.label?.replace(/^Open in /, '') || entity}
          <span style={{ fontWeight: 400, marginLeft: 8 }}>
            {rows.length}{block.more || (block.total != null && block.total > rows.length) ? ` of ${block.total != null ? block.total : 'more'}` : ''}
          </span>
        </div>
        {href && page && (
          <Link to={href} onClick={besideClick(href)} style={{ fontSize: 12, color: theme.primary, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            {linkLabel} <ExternalLink size={12} />
          </Link>
        )}
      </div>
      <RecordsSurfaceProvider value="chat">{table}</RecordsSurfaceProvider>
      {!(entity === 'annotations' && Array.isArray(block.threads) && block.threads.length) && <ChatPager paged={paged} />}
      {popup && <RecordPopup entity={entity} row={popup} teamSlug={teamSlug} onClose={() => setPopup(null)} />}
      {count === 0 && block.open && (
        <div style={{ fontSize: 12, color: theme.textSecondary }}>Nothing matched these filters. {href && <Link to={href} onClick={besideClick(href)} style={{ color: theme.primary }}>Open the page</Link>} to widen them.</div>
      )}
    </div>
  );
}
